import {Component,OnDestroy,OnInit} from '@angular/core';
import {ActivatedRoute,Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {PageHeaderItem} from '@share/component/page-header/page-header';
import {isRouteAllowed} from "@core/security/role-admin-helpers";
import {Session} from "@domain/security/session";
import {Store} from "@ngrx/store";
import {AppState} from "@domain/appstate";
import {filterFirstNotNull} from "@share/utils/rxjs-custom-operator";
import {IComponentWithRoutedTabs} from "@domain/admin/recherche/component-with-routed-tabs";
import {Subscription} from "rxjs";
import {watchRouteParams} from "@share/utils/watch-route-params";

/**
 * Onglets de la page Véhicules
 */
export enum Onglets {
	VEHICULES = 'Vehicules',
	ENERGIES = 'Energies',
	PUISSANCES_FISCALES = 'PuissancesFiscales',
	TYPES_DE_VEHICULES = 'Types',
	PARAMETRES = 'Parametres'
}

/**
 * Composant du menu Entreprise > Véhicules
 */
@Component({
	host: {'data-test-id': 'entreprise-vehicules'},
	templateUrl: './entreprise-vehicules.component.html'
})
export class EntrepriseVehiculesComponent implements OnInit, OnDestroy, IComponentWithRoutedTabs {
	/** Liste statique des onglets */
	public static listeOnglets: Array<PageHeaderItem> = [
		{
			code: Onglets.VEHICULES,
			libelle: 'admin.entreprise.vehicules.onglets.vehicules',
			url: 'Admin/Entreprise/Vehicules/Vehicules'
		},{
			code: Onglets.ENERGIES,
			libelle: 'admin.entreprise.vehicules.onglets.energies',
			url: 'Admin/Entreprise/Vehicules/Energies'
		},{
			code: Onglets.PUISSANCES_FISCALES,
			libelle: 'admin.entreprise.vehicules.onglets.puissances',
			url: 'Admin/Entreprise/Vehicules/PuissancesFiscales'
		},{
			code: Onglets.TYPES_DE_VEHICULES,
			libelle: 'admin.entreprise.vehicules.onglets.types',
			url: 'Admin/Entreprise/Vehicules/Types'
		},{
			code: Onglets.PARAMETRES,
			libelle: 'admin.entreprise.vehicules.onglets.parametres',
			url: 'Admin/Entreprise/Vehicules/Parametres'
		}
	];
	/** Session */
	session: Session;

	/** Liste des onglets */
	listeTabItems: Array<PageHeaderItem> = [];

	/** Onglet courant */
	selectedItem: PageHeaderItem;

	/** Référence vers l'enum des onglets entreprise pour l'utiliser dans le template */
	Onglet = Onglets;

	/** Index de l'onglet courant */
	selectedIndex: number;

	/** Indique si le header doit être affiché */
	isDisplayed: boolean;

	/** Souscription */
	private subscription: Subscription;

	/**
	 * Constructeur
	 *
	 * @param store le store de l'appli
	 * @param translateService le moteur de traduction
	 * @param router le routeur Angular
	 * @param activatedRoute la route actuelle
	 */
	constructor(
		private store: Store<AppState>,
		protected translateService: TranslateService,
		private router: Router,
		private activatedRoute: ActivatedRoute,
	) {
	}

	/**
	 * Initialisation du composant
	 */
	ngOnInit(): void {
		//Récupération de la session
		this.store.select(state => state.session).pipe(filterFirstNotNull()).subscribe((session: Session) => {
			//Stockage de la session
			this.session = session;

			//Création de la liste des onglets
			this.buildListeOnglets();

			//Abonnement aux changements de route
			this.subscription = watchRouteParams(this.router,this.activatedRoute).subscribe(() => {
				//Récupération de la route
				const firstChild = this.activatedRoute.snapshot.firstChild;

				//Indique s'il faut afficher le header
				this.isDisplayed = (Object.values(firstChild.params).findIndex(v => v !== undefined) === -1);

				//Récupération du path
				const segment = firstChild.url[0].path;

				//On vérifie si on trouve l'onglet demandé via l'url
				const matching = Object.values(Onglets).find(v => v === segment);

				if (!!matching) {
					//Si on trouve bien l'onglet correspondant, on met à jour le composant header
					this.selectedItem = this.listeTabItems.find(i => i.code === segment);
					this.selectedIndex = this.listeTabItems.findIndex(i => i.code === segment);
				} else {
					//Sinon, on redirige sur le premier onglet et on met à jour le composant header
					this.selectedItem = this.listeTabItems[0];
					this.selectedIndex = 0;
				}
			});
		});
	}

	/**
	 * Récupération de la liste des onglets autorisés pour l'utilisateur connecté
	 */
	buildListeOnglets(): void {
		//Création de la liste des onglets
		const listeTabItems: PageHeaderItem[] = EntrepriseVehiculesComponent.listeOnglets;

		//Filtrage des onglets en fonction des droits de l'utilisateur connecté
		this.listeTabItems = listeTabItems.filter(tab => isRouteAllowed(this.router,this.session,tab.url));
	}

	/**
	 * Changement d'onglet
	 *
	 * @param selectedItem Onglet sélectionné
	 */
	onSelectedItemChange(selectedItem: PageHeaderItem): void {
		if (!this.selectedItem) {
			this.selectedItem = selectedItem;
		}
		if (selectedItem?.code !== this.selectedItem?.code) {
			this.selectedItem = selectedItem;
			this.router.navigateByUrl(selectedItem.url);
		}
	}

	/**
	 * Destruction du composant
	 */
	ngOnDestroy() {
		//Résiliation de l'abonnement
		this.subscription.unsubscribe();
	}
}
